import dayjs from 'dayjs';
import { v4 } from 'uuid';
import base64 from 'react-native-base64';
import { Dispatch } from 'redux';
import kebabCase from 'lodash/kebabCase';
import {
  AdapterSource,
  MhpDetails,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import DeviceInfoServices from 'services/DeviceInfoServices';
import FileServices from 'services/FileServices';
import { sendAuditLog } from 'store/audit/actions';
import { profileConsumerSelector } from 'store/profile/selectors';
import { Consumer } from 'store/profile/types';
import { RootState, Thunk } from 'store/types';
import { CacheTypes } from 'modules/types/mobileCaching';
import { DFDHeaders } from 'serverTypes/authentication';
import {
  clinicNotesDetailDataSelector,
  clinicNotesListRequestStateSelector
} from 'store/clinicNotes/selectors';
import { messageReply, messageShare } from 'store/messaging/actions';
import {
  AuditAccessRecord,
  AuditAccessTypes,
  AuditActionIds,
  AuditCategory,
  AuditData
} from '../audit/types';
import { TransmitSources } from '../transmit';
import { ClinicNotesDetailData, ClinicNotesFilterType, ClinicNotesSortBy } from './types';

// Clinic Notes List Filter Actions
export const UPDATE_FILTER_OPTIONS = 'UPDATE_FILTER_OPTIONS';
export const updateFilterOptions = (
  id: string,
  checked: boolean,
  filterType: ClinicNotesFilterType
) => ({
  type: UPDATE_FILTER_OPTIONS,
  payload: { id, checked, filterType }
});

export const UPDATE_DATE_RANGE = 'UPDATE_DATE_RANGE';
export const updateDateRange = (from: string, to: string) => ({
  type: UPDATE_DATE_RANGE,
  payload: { from, to }
});

export const APPLY_CLINIC_NOTES_LIST_FILTERS = 'APPLY_CLINIC_NOTES_LIST_FILTERS';
export const RESET_CLINIC_NOTES_LIST_FILTERS = 'RESET_CLINIC_NOTES_LIST_FILTERS';
export const applyClinicNotesListFilters = () => ({ type: APPLY_CLINIC_NOTES_LIST_FILTERS });
export const resetClinicNotesListFilters = () => ({ type: RESET_CLINIC_NOTES_LIST_FILTERS });

// Clinic Notes List Sort Actions
export const UPDATE_SORT = 'UPDATE_SORT';
export const updateSort = (sortBy: ClinicNotesSortBy | string) => ({
  type: UPDATE_SORT,
  payload: { sortBy }
});

// Clinic Notes List Actions
export const GET_CLINIC_NOTES_CACHE_SUCCESS = 'CLINIC_NOTES.GET_CLINIC_NOTES_CACHE_SUCCESS';

export const GET_LIST = 'CLINIC_NOTES.GET_LIST';
export const GET_LIST_SUCCESS = 'CLINIC_NOTES.GET_LIST_SUCCESS';
export const GET_LIST_FAIL = 'CLINIC_NOTES.GET_LIST_FAIL';
export const getClinicNotesList = (useRequestState = true) => ({
  type: GET_LIST,
  payload: {
    useRequestState,
    requestStateSelector: clinicNotesListRequestStateSelector,
    cacheType: CacheTypes.CLINIC_NOTES,
    cacheRetrievalSuccessAction: GET_CLINIC_NOTES_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.CLINIC_DOCUMENTS
      )
    }
  }
});

// Clinic Notes Detail Actions
export const GET_DETAIL = 'CLINIC_NOTES.GET_DETAIL';
export const GET_DETAIL_SUCCESS = 'CLINIC_NOTES.GET_DETAIL_SUCCESS';
export const GET_DETAIL_FAIL = 'CLINIC_NOTES.GET_DETAIL_FAIL';
export const getClinicNotesDetail = (mhpDetails: MhpDetails, acceptType = 'application/pdf') => ({
  type: GET_DETAIL,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      headers: { ...(mhpDetails.headers || {}), [DFDHeaders.accept]: acceptType },
      url: mhpDetails.url
    }
  }
});

/**
 * shareClinicNotes action for transmit
 */
export const SHARE_DOCUMENT = 'CLINIC_NOTES.SHARE_DOCUMENT';
export const SHARE_DOCUMENT_FAIL = `CLINIC_NOTES.SHARE_DOCUMENT_FAIL`;
export const SHARE_DOCUMENT_SUCCESS = `CLINIC_NOTES.SHARE_DOCUMENT_SUCCESS`;

interface ShareData {
  toAddress: string;
  message: string;
  useSecureEmail: boolean;
  contactPhoneNumber: string;
  subject: string;
  documentId?: string;
}

export const shareClinicNoteDocument = (
  _: string,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string
): Thunk => (dispatch: Function, getState: () => RootState) => {
  const { id: documentId, contentType, adapterSource, description, createDate } =
    clinicNotesDetailDataSelector(getState()) || {};
  const data: ShareData = {
    toAddress,
    message,
    useSecureEmail,
    contactPhoneNumber,
    subject: 'Clinic Note Document'
  };

  return dispatch({
    type: SHARE_DOCUMENT,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        url: getDispatcherUrl(
          DispatcherCategoryService.SELF,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.BINARY,
          DispatcherEndpoint.ID_TRANSMIT
        ),
        headers: {
          [DFDHeaders.contentId]: documentId,
          [DFDHeaders.accept]: contentType,
          [DFDHeaders.mhpAdapter]: adapterSource || AdapterSource.UTAH,
          [DFDHeaders.mhpAuditData]: base64.encode(
            JSON.stringify({
              categoryId: AuditCategory.CLINICAL_NOTES_AND_DOCUMENTATION,
              message: description,
              date: dayjs(createDate).format('YYYY/MM/DD')
            })
          )
        },
        data
      }
    }
  });
};

export const replyToClinicNotesDocument = (
  clinicNotesDetail: ClinicNotesDetailData,
  isShare = false,
  returnBackTo?: string,
  disable?: boolean,
  returnBackToParams?: any
): Thunk => dispatch => {
  if (!clinicNotesDetail) {
    return;
  }
  const params = {
    subject: 'Clinical Note Question',
    readonly: ''
  };

  if (isShare) {
    dispatch(
      messageShare({
        ...params,
        transmitParams: {
          source: TransmitSources.ClinicNotes,
          payload: clinicNotesDetail.links[0].documentId,
          returnBackTo,
          returnBackToParams
        }
      })
    );
  } else {
    dispatch(
      messageReply(
        {
          ...params,
          transmitParams: {
            source: TransmitSources.ClinicNotes,
            payload: clinicNotesDetail.links[0].documentId,
            returnBackTo,
            returnBackToParams,
            disable
          }
        },
        clinicNotesDetail?.primaryProvider?.providerId
      )
    );
  }
};

export const RESET_DETAIL = 'CLINIC_NOTES.RESET_DETAIL';

export const resetClinicNotesDetail = () => ({
  type: RESET_DETAIL
});

/**
 * Download Clinic Note Document
 */
export const CLINIC_NOTES_DOWNLOAD_DOCUMENT = 'CLINIC_NOTES.DOWNLOAD_DOCUMENT';
export const CLINIC_NOTES_DOWNLOAD_DOCUMENT_SUCCESS = 'CLINIC_NOTES.DOWNLOAD_DOCUMENT_SUCCESS';
export const CLINIC_NOTES_DOWNLOAD_DOCUMENT_FAIL = 'CLINIC_NOTES.DOWNLOAD_DOCUMENT_FAIL';

export const parseClinicNotesDownloadAuditData = (
  consumer: Consumer,
  description: string
): AuditData => {
  const auditComment = {
    categoryId: AuditCategory.CLINICAL_NOTES_AND_DOCUMENTATION,
    actionBy: `${consumer.lastName}, ${consumer.firstName}`,
    description,
    transmittedTo: '',
    requestId: v4()
  };

  return {
    comment: JSON.stringify(auditComment),
    targetURL: getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.CLINIC_DOCUMENTS,
      DispatcherEndpoint.ID
    ),
    actionId: AuditActionIds.DOWNLOAD,
    mmiNumber: consumer.consumerId,
    accessType: AuditAccessTypes.MILLENNIUM,
    mobileDeviceId: DeviceInfoServices.getDeviceId(),
    accessRecord: AuditAccessRecord.HEALTH_RECORD_SERVICE
  };
};

export const downloadClinicNotesDocument = (
  title: string,
  data: string,
  createDate: string
) => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch({ type: CLINIC_NOTES_DOWNLOAD_DOCUMENT });

  const state = getState();

  const consumer = profileConsumerSelector(state) as Consumer;
  const description = `Downloaded ${title} (${dayjs(createDate).format('YYYY/MM/DD')})`;
  try {
    await FileServices.saveDataToFile(`${kebabCase(title)}-${Date.now().toString()}.pdf`, data);
    const auditData = parseClinicNotesDownloadAuditData(consumer, description);
    dispatch(sendAuditLog(auditData));

    dispatch({ type: CLINIC_NOTES_DOWNLOAD_DOCUMENT_SUCCESS, payload: {} });
  } catch (err) {
    dispatch({ type: CLINIC_NOTES_DOWNLOAD_DOCUMENT_FAIL, payload: {}, error: err });
  }
};
